import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { ROUTE_SINGLE_CANCER_VIEW } from '@/constants/routes';
import { formatRiskDecimal, getRoute } from '@/utils';
import { Box, Flex, IconArrowRight, Link, Text, Badge } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';
import { FactorsBalanceBar } from '../factors-balance-bar/factors-balance-bar';
import { RiskLevel } from '../../dashboard-body.controller';

export type Risk = {
  cancerId: TypeOfCancer; // This ensures cancerId is the correct union type
  name: string;
  level: RiskLevel;
  risk: number;
  increasing: number;
  decreasing: number;
};

type DashboardRisksProps = {
  risks?: Array<Risk>;
  stepSize: number;
};

export default function DashboardRisks({ risks = [], stepSize }: DashboardRisksProps) {
  return (
    <>
      {risks
        .filter(({ cancerId }) => cancerId !== 'overall')
        .map(({ cancerId, name, level, risk, increasing, decreasing }, index) => (
          <Flex
            key={`risk-${index}`}
            justifyContent="space-between"
            alignItems="center"
            gap={3}
            mb="4"
            p="5"
            borderRadius="radii-3"
            border="1px solid"
            borderColor="ui-element-divider"
            bgColor="background-light"
          >
            <Box>
              <Flex alignItems="center" columnGap="1.5" rowGap="0.5" flexWrap="wrap" mb="2">
                <Flex alignItems="center" gap="2">
                  <Link
                    as={ReactRouterLink}
                    variant="link-primary"
                    to={getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: cancerId as string })}
                  >
                    {name}
                  </Link>
                  <Badge {..._getRiskBadgeStyle(level)} size="s">
                    {formatRiskDecimal(risk)}
                  </Badge>
                </Flex>
                <Text variant="text-body-meta-regular">Lifetime Risk</Text>
              </Flex>
              <FactorsBalanceBar increasing={increasing} decreasing={decreasing} stepSize={stepSize} />
            </Box>
            <Link
              as={ReactRouterLink}
              to={getRoute(ROUTE_SINGLE_CANCER_VIEW, { cancerId: cancerId as string })}
              display="flex"
              alignItems="center"
              gap="1"
              bgColor="background-page"
              p="2"
              borderRadius="radii-2"
            >
              <IconArrowRight size="s" />
            </Link>
          </Flex>
        ))}
    </>
  );
}

function _getRiskBadgeStyle(level: RiskLevel) {
  switch (level) {
    case RiskLevel.Below:
      return {
        bgColor: 'var(--chakra-colors-alert-success-default)',
        color: 'var(--chakra-colors-text-success)',
      };
    case RiskLevel.Average:
      return {
        bgColor: 'var(--chakra-colors-alert-warning-default)',
        color: 'var(--chakra-colors-text-warning)',
      };
    case RiskLevel.Above:
      return {
        bgColor: 'var(--chakra-colors-alert-error-default)',
        color: 'var(--chakra-colors-text-error)',
      };
  }
}
